
import React, { useState } from 'react';

const FlanexusEn: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 flex items-center justify-center gap-2 md:h-20 bg-[#071C74] px-4 py-4 font-medium md:flex-none md:justify-start md:p-2 md:px-3">
        <div className="sticky top-0 flex h-7 items-center justify-between md:h-20 w-full max-w-7xl mx-auto">
          <div className="adi-header-left">
            <a href="/">
              <img 
                alt="Flanexus Logo" 
                width={125} 
                height={56} 
                src="https://static.readdy.ai/image/4d7c1148f372075ad366b96364633fcd/3900b388860a5efa532a02080a385ec7.png" 
                className="h-14 w-auto"
              />
            </a>
          </div>
          <div className="hidden md:flex items-center space-x-4 text-white">
            <div className="relative">
              <div className="flex bg-white/10 backdrop-blur-sm rounded-full p-1">
                <a 
                  href="/flanexus"
                  className="px-4 py-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                >
                  简体中文
                </a>
                <button 
                  className="px-4 py-2 text-white bg-white/20 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap"
                  onClick={() => {
                    setSelectedLanguage('English');
                    setIsLanguageDropdownOpen(false);
                  }}
                >
                  English
                </button>
              </div>
            </div>
            <a 
              href="/flanexus/en"
              className="px-4 py-2 text-white hover:text-blue-200 transition-colors"
            >
              Flanexus
            </a>
            <a 
              href="/en/news"
              className="px-4 py-2 text-white hover:text-blue-200 transition-colors"
            >
              News
            </a>
            <a 
              href="/en/contact-us"
              className="px-4 py-2 border border-white rounded-full hover:bg-white hover:text-[#071C74] transition-colors text-white whitespace-nowrap"
            >
              Contact Us
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative min-h-[600px] bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center"
        style={{
          backgroundImage: "url('https://public.readdy.ai/ai/img_res/bf55232ef5814455370b7b1b5dc99800.jpg')"
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium">
                <i className="ri-global-line w-4 h-4 flex items-center justify-center mr-2"></i>
                Empowering Enterprise Digital Transformation
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-white leading-tight">
                Technology Empowers<br />
                <span className="text-orange-400">Global Market Expansion</span>
              </h1>
              
              <p className="text-xl text-white/90 leading-relaxed max-w-lg">
                Flanexus is committed to helping enterprises enhance their core competitiveness in an increasingly challenging environment through industry-leading digital solutions and deep business understanding.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="/en/contact-us"
                  className="bg-orange-600 text-white px-8 py-4 rounded-lg hover:bg-orange-700 transition-colors font-semibold whitespace-nowrap text-center"
                >
                  Free Consultation
                </a>
                <a 
                  href="https://www.flanexus.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-blue-900 transition-colors font-semibold whitespace-nowrap text-center"
                >
                  Learn More
                </a>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20business%20technology%20illustration%20showing%20global%20expansion%2C%20digital%20transformation%20concept%2C%20modern%20corporate%20graphics%20with%20world%20map%20connections%2C%20data%20visualization%20elements%2C%20clean%20professional%20design%20with%20blue%20and%20orange%20color%20scheme&width=600&height=500&seq=flanexus-hero-img-en&orientation=landscape"
                  alt="Flanexus Digital Solutions" 
                  className="w-full rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Growth Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Empowering Multi-Industry Enterprise Growth</h2>
          </div>
          
          <div className="relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <div className="space-y-6">
                  <div className="inline-flex items-center px-4 py-2 bg-orange-100 rounded-full">
                    <span className="text-orange-600 font-semibold">Retail E-commerce</span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">Global Retail Digital Transformation</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Facing the inevitable trend of business digitalization, many retail enterprises have already or are in the process of digital technology deployment to support their digital transformation needs. However, in the process of transforming from traditional retail to digital retail, enterprises still face many urgent problems to be solved.
                  </p>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Flanexus has rich successful cases of retail enterprise digital transformation, providing comprehensive solutions including brand DTC platform construction, digital marketing, membership management, order and product management.
                  </p>
                  <button className="inline-flex items-center text-orange-600 hover:text-orange-7  font-semibold">
                    Explore More
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20retail%20digital%20transformation%20illustration%2C%20e-commerce%20platform%20interface%2C%20global%20retail%20technology%2C%20professional%20business%20graphics%20showing%20online%20shopping%2C%20digital%20marketing%2C%20customer%20data%20management%2C%20clean%20corporate%20design&width=600&height=400&seq=flanexus-retail-en&orientation=landscape"
                  alt="Retail Digital Transformation" 
                  className="w-full rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Our Products</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: "ri-global-line",
                title: "Brand Expansion Consulting",
                tag: "Consulting Services",
                features: ["In-depth market analysis and strategy development", "Personalized expansion path planning", "Localization operation guidance"],
                link: "#"
              },
              {
                icon: "ri-shopping-cart-line",
                title: "D2C E-commerce System",
                tag: "E-commerce Platform",
                features: ["Brand DTC mall construction", "Private domain operation platform", "Global e-commerce business deployment"],
                link: "#"
              },
              {
                icon: "ri-settings-3-line",
                title: "Omnichannel OMS System",
                tag: "Order Management",
                features: ["Unified order management", "Multi-channel inventory synchronization", "Intelligent fulfillment optimization"],
                link: "#"
              },
              {
                icon: "ri-bar-chart-line",
                title: "Data Analytics Platform",
                tag: "Data Insights",
                features: ["Real-time data monitoring", "Precise user profiling", "Business growth analysis"],
                link: "#"
              },
              {
                icon: "ri-customer-service-line",
                title: "Localization Services",
                tag: "Localization",
                features: ["Cultural adaptability analysis", "Local operation support", "Local compliance guidance"],
                link: "#"
              },
              {
                icon: "ri-rocket-line",
                title: "Digital Marketing",
                tag: "Marketing Promotion",
                features: ["Comprehensive marketing strategy", "Brand awareness enhancement", "ROI optimization management"],
                link: "#"
              }
            ].map((product, index) => (
              <div key={index} className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border hover:border-orange-200">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <i className={`${product.icon} text-white text-2xl`}></i>
                  </div>
                  <span className="px-3 py-1 bg-orange-100 text-orange-600 text-sm font-medium rounded-full">
                    {product.tag}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors">
                  {product.title}
                </h3>
                
                <div className="space-y-2 mb-6">
                  {product.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-gray-600">
                      <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-3"></span>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Industries We Serve</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Focused on providing professional global expansion solutions for brands across different industries
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {[
              {
                icon: "ri-store-line",
                title: "Retail",
                description: "Omnichannel retail solutions to help traditional retail brands achieve digital transformation"
              },
              {
                icon: "ri-magic-line", 
                title: "Beauty",
                description: "Beauty brand localization marketing strategies to precisely reach target consumer groups"
              },
              {
                icon: "ri-shirt-line",
                title: "Fashion",
                description: "Fashion brand global expansion to build international brand image"
              },
              {
                icon: "ri-t-shirt-line",
                title: "Apparel",
                description: "Apparel brand overseas operations to establish complete supply chain systems"
              },
              {
                icon: "ri-bear-smile-line",
                title: "Mother & Baby",
                description: "Mother & baby product safety compliance to build consumer trust and brand loyalty"
              }
            ].map((industry, index) => (
              <div key={index} className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border hover:border-orange-200 text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-red-100 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform mb-6 mx-auto">
                  <i className={`${industry.icon} text-orange-600 text-2xl`}></i>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-orange-600 transition-colors">
                  {industry.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {industry.description}
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Growth Data Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Grow Rapidly with Flanexus</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <img 
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAIAAAACACAMAAAD04JH5AAAAilBMVEUAAAD////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////2N2iNAAAALXRSTlMAj6ZnoBFFS3PqCseWLhuD9uVTIgT7h2so7q94NdbL3rxgXT0dCNnYsH0Ufm2Ro2j6AAABsklEQVR42u3WyXKCUBCF4SYBNIrmgkwyaJwSNen3f72glRQBbgoX3mbh+bZUUf/i0AUBAAAAwB2k+VStv2ggRVzyVU7yQtfz+dcLCZsE9pL/mpAgZ5sprgkHzJO3iGvCAWm+4pp0QBF/cEekBALqybdEb8n8yWxAPfkOlW2d6qHxAGeTKe5Y2sGBKqYD9trJ+54bUkUbYHzyZVxQxXjAXDd5XuUpVQQCiqV28lSRCZh2Jr+5TF4s4BC1Jn95v2SAo5+8fMB7Y/LyAU/UIB9gIwABtwe8/KvMQ8MBvbyhA3gxdIA7dMDoEQLKqc5aLsAlnWcEIAABCEAAAhBwr4DA8pqsQDQg465MMGDDOlu5AJt1bLkAi3ViuYCF4q7lQnCEu3duK3eyd+A8bzo/2iFCAAIQMHiAe3ptOu1EA2zu8gQDAtYJhv4f8OQCPNax5AJmirtUKjjCkc9t/kj0MwzTWVMaPtghQgACEIAABCAAAYIBh8VFwVercb8VX1nPOj91yezCoRt8KjYmikPqM2ajxtTHZ6N86qPYKEV9jmzUkfrs12zQek+9JollTDIhAAAAgLZvaSHLU9A65hoAAAAASUVORK5CYII="
                  alt="Brands and Merchants" 
                  className="w-10 h-10"
                />
              </div>
              <div className="text-5xl font-bold text-orange-400 mb-2">20+</div>
              <div className="text-gray-300">Brands Served</div>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <img 
                  src="https://gtechdigi.cn/_nuxt/img/data_icon2.5abaa75.png"
                  alt="Southeast Asia Experience" 
                  className="w-10 h-10"
                />
              </div>
              <div className="text-5xl font-bold text-orange-400 mb-2">8+</div>
              <div className="text-gray-300">Years Southeast Asia Experience</div>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <img 
                  src="https://gtechdigi.cn/_nuxt/img/data_icon3.09f1aa6.png" 
                  alt="Industry Experience" 
                  className="w-10 h-10"
                />
              </div>
              <div className="text-5xl font-bold text-orange-400 mb-2">10+</div>
              <div className="text-gray-300">Years of Industry Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="flex items-center space-x-6 text-sm text-gray-400">
                <span>Copyright © 2019-2025 Flanexus. All Rights Reserved. Flanexus ® 版权所有</span>
                <span>|</span>
                <a href="#" className="hover:text-orange-400 transition-colors">Terms of Service</a>
                <span>|</span>
                <a href="#" className="hover:text-orange-400 transition-colors">Privacy Policy</a>
              </div>
              
              <div className="flex items-center space-x-4 text-sm text-gray-400">
                <span>Information Security Management System Certification-ISO27001</span>
                <span>|</span>
                <a 
                  href="https://readdy.ai/?origin=logo" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-orange-400 transition-colors"
                >
                  Powered by Readdy
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FlanexusEn;
